import React, { Component } from 'react';
import startCase from 'lodash/startCase';
import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import { FormGroup, TextInput, Select, PagerButtons } from 'sure-components';
import PropTypes from 'prop-types';
import AddressForm from 'shared/lib/components/AddressForm';
import PlanDisclaimer from 'shared/lib/components/PlanDisclaimer';
import Cleave from 'cleave.js/dist/cleave-react';
import * as analytics from 'shared/lib/analytics';
import VERTICALS from 'shared/lib/constants/verticals';
import './CollectiblesEndorsementPropertyInfo.css';

const propTypes = {
  account: PropTypes.object,
  activeModal: PropTypes.string,
  categoryCode: PropTypes.string,
  categorySlug: PropTypes.string,
  disclaimer: PropTypes.string,
  exclusionNotice: PropTypes.string,
  getPoliciesForVertical: PropTypes.func,
  isCollectiblesEndorsementInProgress: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  policiesById: PropTypes.object,
  propertyAddress: PropTypes.object,
  resetCollectiblesEndorsementData: PropTypes.func,
  responses: PropTypes.object,
  saveResponse: PropTypes.func,
  shouldPreserveAddress: PropTypes.bool,
  onSubmitAddress: PropTypes.func,
  updateCollectiblesEndorsementAddress: PropTypes.func,
};

const defaultProps = {
  account: {},
  activeModal: null,
  disclaimer: null,
  isCollectiblesEndorsementInProgress: false,
  isLoggedIn: false,
  exclusionNotice: null,
  propertyAddress: {},
  responses: {},
  shouldPreserveAddress: false,
};

class CollectiblesEndorsementPropertyInfo extends Component {
  state = {
    isSubmitting: false,
  };

  componentDidMount() {
    analytics.page('Collectibles - Current Collectibles Address', {
      categoryCode: this.props.categoryCode,
    });

    if (this.props.isLoggedIn) {
      this.props.getPoliciesForVertical(VERTICALS.collectibles.code);
      this.props.saveResponse({
        owner_first_name: this.props.account.firstName,
        owner_last_name: this.props.account.lastName,
        owner_phone_number: (this.props.account.phoneNumber || '').slice(-10),
        owner_email: this.props.account.email,
      });
    }
  }

  handleNext = e => {
    if (e) {
      e.preventDefault();
    }

    this.setState({ isSubmitting: true });
    return this.residenceAddressComponent.getGeocodedValue().then(location => {
      this.props.updateCollectiblesEndorsementAddress({
        lat: location.lat,
        lng: location.lng,
        country: location.country,
        postal: location.postal,
      });
      this.setState({ isSubmitting: false });
      return this.props.onSubmitAddress();
    });
  };

  handleInputChange = e => {
    this.props.saveResponse({
      [e.target.name]: e.target.value,
    });
  };

  get categoryTitle() {
    if (this.props.categoryCode === 'jewelry') {
      return 'Jewelry & Watches';
    } else if (this.props.categorySlug !== 'other-insurance') {
      return startCase(this.props.categoryCode.replace('_', ' '));
    } else {
      return 'Collectibles';
    }
  }

  get isNextDisabled() {
    const { propertyAddress, responses } = this.props;

    return (
      some(
        [
          propertyAddress.streetAddress,
          propertyAddress.city,
          propertyAddress.region,
          propertyAddress.postal,
          responses.owner_first_name,
          responses.owner_last_name,
          responses.owner_phone_number,
          responses.owner_email,
        ],
        isEmpty
      ) ||
      (responses.owner_phone_number &&
        responses.owner_phone_number.match(/\d/g).length !== 10)
    );
  }

  render() {
    return (
      <div className="CollectiblesEndorsementPropertyInfo">
        <div className="CollectiblesEndorsementPropertyInfo-intro">
          <div className="Container">
            <div className="CollectiblesEndorsementPropertyInfo-intro-icon">
              <i className="CollectiblesEndorsementPropertyInfo-intro-icon-icon material-icons">
                widgets
              </i>
            </div>
            <h1 className="CollectiblesEndorsementPropertyInfo-intro-heading">
              {this.categoryTitle} Insurance
            </h1>
            <p className="CollectiblesEndorsementPropertyInfo-intro-info">
              Tell us about you and your collection. Earn discounts based on
              your details. Get your quote, then get your policy.
            </p>
          </div>
        </div>
        <div className="CollectiblesEndorsementPropertyInfo-address">
          <div className="Container">
            <h2 className="CollectiblesEndorsementPropertyInfo-address-heading">
              Where is your collection located?
            </h2>
          </div>
          <div className="Container Container--small">
            <form
              className="CollectiblesEndorsementPropertyInfo-address-form"
              onSubmit={this.handleNext}
            >
              <AddressForm
                streetAddress={this.props.propertyAddress.streetAddress}
                unit={this.props.propertyAddress.unit}
                city={this.props.propertyAddress.city}
                region={this.props.propertyAddress.region}
                postal={this.props.propertyAddress.postal}
                country={this.props.propertyAddress.country}
                ref={c => (this.residenceAddressComponent = c)}
                onChange={this.props.updateCollectiblesEndorsementAddress}
              />
              <FormGroup>
                <TextInput
                  name="owner_first_name"
                  placeholder="First Name"
                  required
                  value={this.props.responses.owner_first_name || ''}
                  maxLength="20"
                  pattern="[a-zA-Z ]+"
                  onChange={this.handleInputChange}
                />
                <TextInput
                  name="owner_middle_name"
                  placeholder="Middle Name (optional)"
                  value={this.props.responses.owner_middle_name || ''}
                  maxLength="20"
                  pattern="[a-zA-Z ]*"
                  onChange={this.handleInputChange}
                />
                <TextInput
                  name="owner_last_name"
                  placeholder="Last Name"
                  required
                  value={this.props.responses.owner_last_name || ''}
                  maxLength="20"
                  pattern="[a-zA-Z ]+"
                  onChange={this.handleInputChange}
                />
                <Select
                  name="owner_suffix"
                  value={this.props.responses.owner_suffix || ''}
                  onChange={this.handleInputChange}
                >
                  <option value="">Suffix (optional)</option>
                  <option value="Jr.">Jr.</option>
                  <option value="Sr.">Sr.</option>
                  <option value="II">II</option>
                  <option value="III">III</option>
                  <option value="IV">IV</option>
                  <option value="V">V</option>
                  <option value="VI">VI</option>
                  <option value="VII">VII</option>
                  <option value="VIII">VIII</option>
                  <option value="IX">IX</option>
                  <option value="X">X</option>
                </Select>
                <Cleave
                  className="SureTextInput"
                  name="owner_phone_number"
                  placeholder="Phone Number (e.g. 555-555-5555)"
                  required={true}
                  options={{
                    blocks: [3, 3, 4],
                    delimiter: '-',
                    numericOnly: true,
                  }}
                  style={{ borderRadius: '0', margin: '0', borderTop: '0' }}
                  value={this.props.responses.owner_phone_number}
                  onChange={this.handleInputChange}
                />
                <TextInput
                  type="email"
                  name="owner_email"
                  placeholder="Email Address"
                  required
                  value={this.props.responses.owner_email || ''}
                  onChange={this.handleInputChange}
                />
              </FormGroup>
              <input
                type="submit"
                style={{ display: 'none' }}
                ref={c => (this.inputSubmit = c)}
              />
            </form>
            <PagerButtons
              onNext={() => this.inputSubmit.click()}
              isNextDisabled={this.state.isSubmitting}
              isBackDisabled={true}
              isInProgress={
                this.props.isCollectiblesEndorsementInProgress ||
                this.state.isSubmitting
              }
            />
            <PlanDisclaimer text={this.props.disclaimer} />
          </div>
        </div>
      </div>
    );
  }
}

CollectiblesEndorsementPropertyInfo.propTypes = propTypes;
CollectiblesEndorsementPropertyInfo.defaultProps = defaultProps;

export default CollectiblesEndorsementPropertyInfo;
